/**
 * Timetable Management - Full Featured Page
 * Tabs: Class Timetable, Teacher Schedule, Period Setup
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = {
  get: async (endpoint, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return await res.json();
    } catch (err) {
      return { error: err.message };
    }
  },
  post: async (endpoint, data, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        return { error: errData.detail || `Error ${res.status}`, status: res.status };
      }
      return await res.json();
    } catch (err) {
      return { error: err.message };
    }
  },
  delete: async (endpoint, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return await res.json();
    } catch (err) {
      return { error: err.message };
    }
  }
};

const DAYS = [
  { key: 'monday', label: 'Monday', labelHi: 'सोमवार' },
  { key: 'tuesday', label: 'Tuesday', labelHi: 'मंगलवार' },
  { key: 'wednesday', label: 'Wednesday', labelHi: 'बुधवार' },
  { key: 'thursday', label: 'Thursday', labelHi: 'गुरुवार' },
  { key: 'friday', label: 'Friday', labelHi: 'शुक्रवार' },
  { key: 'saturday', label: 'Saturday', labelHi: 'शनिवार' },
];

const SUBJECT_COLORS = [
  'bg-blue-100 text-blue-700 border-blue-200',
  'bg-green-100 text-green-700 border-green-200',
  'bg-purple-100 text-purple-700 border-purple-200',
  'bg-pink-100 text-pink-700 border-pink-200',
  'bg-yellow-100 text-yellow-700 border-yellow-200',
  'bg-cyan-100 text-cyan-700 border-cyan-200',
  'bg-orange-100 text-orange-700 border-orange-200',
  'bg-red-100 text-red-700 border-red-200',
  'bg-indigo-100 text-indigo-700 border-indigo-200',
  'bg-teal-100 text-teal-700 border-teal-200',
];

function getSubjectColor(subjectId, colorMap) {
  if (!colorMap[subjectId]) {
    colorMap[subjectId] = SUBJECT_COLORS[Object.keys(colorMap).length % SUBJECT_COLORS.length];
  }
  return colorMap[subjectId];
}

// ==================== TIMETABLE GRID TAB ====================
function TimetableGridTab({ token, language }) {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [periods, setPeriods] = useState([]);
  const [timetable, setTimetable] = useState({});
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editCell, setEditCell] = useState(null);
  const [editForm, setEditForm] = useState({ subject_id: '', subject_name: '', teacher_id: '', teacher_name: '', room: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [colorMap] = useState({});

  useEffect(() => {
    (async () => {
      const [cls, per, sub, tch] = await Promise.all([
        api.get('/api/classes/', token),
        api.get('/api/timetable/periods', token),
        api.get('/api/exams/subjects', token),
        api.get('/api/teachers/', token),
      ]);
      setClasses(cls.classes || []);
      setPeriods(per.periods || []);
      setSubjects(sub.subjects || []);
      setTeachers(tch.teachers || []);
      setLoading(false);
    })();
  }, [token]);

  const loadTimetable = useCallback(async () => {
    if (!selectedClass || !selectedSection) return;
    const res = await api.get(`/api/timetable/class/${selectedClass}/${selectedSection}`, token);
    setTimetable(res.by_day || {});
  }, [selectedClass, selectedSection, token]);

  useEffect(() => { loadTimetable(); }, [loadTimetable]);

  const handleCellClick = (day, period) => {
    const existing = (timetable[day] || []).find(e => e.period === period.period);
    if (existing) {
      setEditForm({
        subject_id: existing.subject_id || '',
        subject_name: existing.subject_name || '',
        teacher_id: existing.teacher_id || '',
        teacher_name: existing.teacher_name || '',
        room: existing.room || ''
      });
    } else {
      setEditForm({ subject_id: '', subject_name: '', teacher_id: '', teacher_name: '', room: '' });
    }
    setEditCell({ day, period: period.period });
  };

  const handleSubjectChange = (subjectId) => {
    const sub = subjects.find(s => s.id === subjectId);
    setEditForm(prev => ({ ...prev, subject_id: subjectId, subject_name: sub?.name || '' }));
  };

  const handleTeacherChange = (teacherId) => {
    const tch = teachers.find(t => t.id === teacherId);
    setEditForm(prev => ({ ...prev, teacher_id: teacherId, teacher_name: tch?.name || '' }));
  };

  const handleSaveEntry = async () => {
    if (!editCell || !editForm.subject_id) return;
    setSaving(true);

    const params = new URLSearchParams({
      class_id: selectedClass,
      section_id: selectedSection,
      day: editCell.day,
      period: editCell.period.toString(),
      subject_id: editForm.subject_id,
      subject_name: editForm.subject_name,
    });
    if (editForm.teacher_id) params.append('teacher_id', editForm.teacher_id);
    if (editForm.teacher_name) params.append('teacher_name', editForm.teacher_name);
    if (editForm.room) params.append('room', editForm.room);

    const res = await api.post(`/api/timetable/entry?${params.toString()}`, {}, token);
    setSaving(false);

    if (res.error) {
      setToast({ message: res.error, type: 'error' });
      setTimeout(() => setToast(null), 4000);
    } else {
      setEditCell(null);
      loadTimetable();
      setToast({ message: language === 'hi' ? 'सेव हो गया!' : 'Saved!', type: 'success' });
      setTimeout(() => setToast(null), 2000);
    }
  };

  const handleDeleteEntry = async () => {
    if (!editCell) return;
    const params = new URLSearchParams({
      class_id: selectedClass,
      section_id: selectedSection,
      day: editCell.day,
      period: editCell.period.toString(),
    });
    await api.delete(`/api/timetable/entry?${params.toString()}`, token);
    setEditCell(null);
    loadTimetable();
  };

  const sections = classes.find(c => c.id === selectedClass)?.sections || [];

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="timetable-grid-tab">
      {/* Class/Section Selector */}
      <div className="bg-white rounded-xl border p-4">
        <div className="flex flex-wrap gap-3">
          <select data-testid="tt-class-select" value={selectedClass}
            onChange={(e) => { setSelectedClass(e.target.value); setSelectedSection(''); setTimetable({}); }}
            className="px-3 py-2 border rounded-lg text-sm bg-white">
            <option value="">{language === 'hi' ? 'कक्षा चुनें' : 'Select Class'}</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select data-testid="tt-section-select" value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm bg-white" disabled={!selectedClass}>
            <option value="">{language === 'hi' ? 'सेक्शन' : 'Section'}</option>
            {sections.map(s => <option key={s.id} value={s.id}>Section {s.name}</option>)}
          </select>
        </div>
      </div>

      {/* Timetable Grid */}
      {selectedClass && selectedSection && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="timetable-grid">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th className="px-3 py-3 text-left text-xs font-semibold w-24">
                    {language === 'hi' ? 'दिन / पीरियड' : 'Day / Period'}
                  </th>
                  {periods.map(p => (
                    <th key={p.period} className="px-2 py-3 text-center text-xs font-semibold min-w-[120px]">
                      <div>{language === 'hi' ? `पीरियड ${p.period}` : `Period ${p.period}`}</div>
                      <div className="text-[10px] font-normal opacity-70">{p.start_time} - {p.end_time}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {DAYS.map(day => (
                  <tr key={day.key} className="border-b hover:bg-gray-50/50">
                    <td className="px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-50 border-r">
                      {language === 'hi' ? day.labelHi : day.label}
                    </td>
                    {periods.map(p => {
                      const entry = (timetable[day.key] || []).find(e => e.period === p.period);
                      const isBreak = p.start_time === '10:15' || p.start_time === '12:45';
                      const isEditing = editCell?.day === day.key && editCell?.period === p.period;

                      if (isBreak && p.period === periods.findIndex(pp => pp.start_time === '10:15') + 1) {
                        // We won't skip - just mark visually if it's a break slot
                      }

                      return (
                        <td key={p.period}
                          className={`px-1 py-1 text-center cursor-pointer transition border-r ${isEditing ? 'ring-2 ring-orange-400 bg-orange-50' : ''}`}
                          onClick={() => handleCellClick(day.key, p)}
                          data-testid={`cell-${day.key}-${p.period}`}
                        >
                          {entry ? (
                            <div className={`rounded-lg p-1.5 text-xs border ${getSubjectColor(entry.subject_id, colorMap)}`}>
                              <div className="font-semibold truncate">{entry.subject_name}</div>
                              {entry.teacher_name && (
                                <div className="text-[10px] opacity-70 truncate mt-0.5">{entry.teacher_name}</div>
                              )}
                              {entry.room && (
                                <div className="text-[10px] opacity-50">{entry.room}</div>
                              )}
                            </div>
                          ) : (
                            <div className="h-12 flex items-center justify-center text-gray-300 hover:text-gray-400 transition">
                              <span className="text-lg">+</span>
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Panel */}
      {editCell && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setEditCell(null)}>
          <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-md mx-4 space-y-4" onClick={e => e.stopPropagation()} data-testid="edit-entry-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">
                {language === 'hi'
                  ? `${DAYS.find(d => d.key === editCell.day)?.labelHi} - पीरियड ${editCell.period}`
                  : `${DAYS.find(d => d.key === editCell.day)?.label} - Period ${editCell.period}`}
              </h3>
              <button onClick={() => setEditCell(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <select data-testid="edit-subject-select" value={editForm.subject_id}
              onChange={(e) => handleSubjectChange(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm bg-white">
              <option value="">{language === 'hi' ? 'विषय चुनें *' : 'Select Subject *'}</option>
              {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>

            <select data-testid="edit-teacher-select" value={editForm.teacher_id}
              onChange={(e) => handleTeacherChange(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm bg-white">
              <option value="">{language === 'hi' ? 'शिक्षक चुनें' : 'Select Teacher'}</option>
              {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>

            <input data-testid="edit-room-input" placeholder={language === 'hi' ? 'कमरा (वैकल्पिक)' : 'Room (optional)'}
              value={editForm.room} onChange={(e) => setEditForm(prev => ({ ...prev, room: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg text-sm" />

            <div className="flex gap-2">
              <button data-testid="save-entry-btn" onClick={handleSaveEntry} disabled={saving || !editForm.subject_id}
                className="flex-1 py-2.5 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50 transition">
                {saving ? '...' : (language === 'hi' ? 'सेव करें' : 'Save')}
              </button>
              <button data-testid="delete-entry-btn" onClick={handleDeleteEntry}
                className="px-4 py-2.5 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition">
                {language === 'hi' ? 'हटाएं' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {!selectedClass && (
        <div className="text-center py-16 text-gray-400 bg-white rounded-xl border">
          <div className="text-5xl mb-3">📅</div>
          <p className="text-lg">{language === 'hi' ? 'कक्षा और सेक्शन चुनें' : 'Select a class and section'}</p>
        </div>
      )}

      {toast && (
        <div className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg text-white text-sm z-50 ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}

// ==================== TEACHER SCHEDULE TAB ====================
function TeacherScheduleTab({ token, language }) {
  const [schedules, setSchedules] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTeacher, setExpandedTeacher] = useState(null);

  useEffect(() => {
    (async () => {
      const [sch, per] = await Promise.all([
        api.get('/api/timetable/all-teachers-schedule', token),
        api.get('/api/timetable/periods', token),
      ]);
      setSchedules(sch.teachers || []);
      setPeriods(per.periods || []);
      setLoading(false);
    })();
  }, [token]);

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="teacher-schedule-tab">
      {schedules.length > 0 ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {schedules.map(t => (
              <div key={t.teacher_id}
                className={`bg-white rounded-xl border p-4 cursor-pointer transition hover:shadow-md ${expandedTeacher === t.teacher_id ? 'ring-2 ring-orange-400' : ''}`}
                onClick={() => setExpandedTeacher(expandedTeacher === t.teacher_id ? null : t.teacher_id)}
                data-testid={`teacher-card-${t.teacher_id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center font-bold text-sm">
                    {t.teacher_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-800">{t.teacher_name}</div>
                    <div className="text-xs text-gray-500">
                      {language === 'hi' ? `${t.total_periods} पीरियड/सप्ताह` : `${t.total_periods} periods/week`}
                    </div>
                  </div>
                  <span className="text-gray-400">{expandedTeacher === t.teacher_id ? '▲' : '▼'}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Expanded Teacher Schedule */}
          {expandedTeacher && (() => {
            const teacher = schedules.find(t => t.teacher_id === expandedTeacher);
            if (!teacher) return null;
            return (
              <div className="bg-white rounded-xl border overflow-hidden" data-testid="teacher-schedule-grid">
                <div className="p-4 border-b bg-gray-50">
                  <h3 className="font-semibold text-gray-800">{teacher.teacher_name} - {language === 'hi' ? 'साप्ताहिक शेड्यूल' : 'Weekly Schedule'}</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-800 text-white">
                        <th className="px-3 py-2 text-left text-xs">{language === 'hi' ? 'दिन' : 'Day'}</th>
                        {periods.map(p => (
                          <th key={p.period} className="px-2 py-2 text-center text-xs min-w-[100px]">
                            P{p.period}<br/><span className="text-[10px] opacity-70">{p.start_time}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {DAYS.map(day => (
                        <tr key={day.key} className="border-b">
                          <td className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 border-r">
                            {language === 'hi' ? day.labelHi : day.label}
                          </td>
                          {periods.map(p => {
                            const entry = (teacher.schedule[day.key] || []).find(e => e.period === p.period);
                            return (
                              <td key={p.period} className="px-1 py-1 text-center border-r">
                                {entry ? (
                                  <div className="bg-blue-50 text-blue-700 rounded-md p-1 text-xs border border-blue-200">
                                    <div className="font-medium truncate">{entry.subject_name}</div>
                                    <div className="text-[10px] opacity-70">{entry.class_name}</div>
                                  </div>
                                ) : (
                                  <div className="h-8 text-gray-200 flex items-center justify-center text-xs">-</div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {!expandedTeacher && (
            <p className="text-center text-sm text-gray-400">
              {language === 'hi' ? 'शिक्षक पर क्लिक करें शेड्यूल देखने के लिए' : 'Click on a teacher to view their schedule'}
            </p>
          )}
        </>
      ) : (
        <div className="text-center py-16 text-gray-400 bg-white rounded-xl border">
          <div className="text-5xl mb-3">👩‍🏫</div>
          <p>{language === 'hi' ? 'कोई शिक्षक शेड्यूल नहीं' : 'No teacher schedules found'}</p>
          <p className="text-xs mt-1">{language === 'hi' ? 'पहले टाइमटेबल बनाएं' : 'Create timetable entries first'}</p>
        </div>
      )}
    </div>
  );
}

// ==================== PERIOD SETUP TAB ====================
function PeriodSetupTab({ token, language }) {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    (async () => {
      const res = await api.get('/api/timetable/periods', token);
      setPeriods(res.periods || []);
      setLoading(false);
    })();
  }, [token]);

  const handleUpdate = (index, field, value) => {
    const updated = [...periods];
    updated[index] = { ...updated[index], [field]: value };
    setPeriods(updated);
  };

  const addPeriod = () => {
    const last = periods[periods.length - 1];
    setPeriods([...periods, {
      period: periods.length + 1,
      start_time: last?.end_time || '08:00',
      end_time: '',
      is_break: false
    }]);
  };

  const removePeriod = (index) => {
    const updated = periods.filter((_, i) => i !== index);
    updated.forEach((p, i) => { if (!p.is_break) p.period = i + 1; });
    setPeriods(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    const res = await api.post('/api/timetable/periods', periods, token);
    setSaving(false);
    if (res.success) {
      setToast({ message: language === 'hi' ? 'पीरियड टाइमिंग सेव हो गई!' : 'Period timings saved!', type: 'success' });
    } else {
      setToast({ message: res.error || 'Error', type: 'error' });
    }
    setTimeout(() => setToast(null), 3000);
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="period-setup-tab">
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-800">{language === 'hi' ? 'पीरियड टाइमिंग सेटअप' : 'Period Timing Setup'}</h3>
            <p className="text-xs text-gray-500 mt-1">{language === 'hi' ? 'अपने स्कूल की टाइमिंग कस्टमाइज करें' : 'Customize your school timings'}</p>
          </div>
          <button data-testid="add-period-btn" onClick={addPeriod}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition">
            + {language === 'hi' ? 'पीरियड जोड़ें' : 'Add Period'}
          </button>
        </div>

        <div className="p-4 space-y-3">
          {periods.map((p, idx) => (
            <div key={idx} className={`flex items-center gap-3 p-3 rounded-lg border ${p.is_break ? 'bg-yellow-50 border-yellow-200' : 'bg-white'}`}
              data-testid={`period-row-${idx}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm ${p.is_break ? 'bg-yellow-200 text-yellow-700' : 'bg-orange-100 text-orange-600'}`}>
                {p.is_break ? '☕' : `P${p.period}`}
              </div>
              <input type="time" value={p.start_time || ''} onChange={(e) => handleUpdate(idx, 'start_time', e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm" data-testid={`period-start-${idx}`} />
              <span className="text-gray-400">→</span>
              <input type="time" value={p.end_time || ''} onChange={(e) => handleUpdate(idx, 'end_time', e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm" data-testid={`period-end-${idx}`} />
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input type="checkbox" checked={p.is_break || false}
                  onChange={(e) => handleUpdate(idx, 'is_break', e.target.checked)}
                  className="w-4 h-4 accent-yellow-500" />
                {language === 'hi' ? 'ब्रेक' : 'Break'}
              </label>
              <button onClick={() => removePeriod(idx)} className="ml-auto text-red-400 hover:text-red-600 text-lg">✕</button>
            </div>
          ))}
        </div>

        <div className="p-4 border-t bg-gray-50">
          <button data-testid="save-periods-btn" onClick={handleSave} disabled={saving}
            className="w-full py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50 transition">
            {saving ? '...' : (language === 'hi' ? 'टाइमिंग सेव करें' : 'Save Timings')}
          </button>
        </div>
      </div>

      {toast && (
        <div className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg text-white text-sm z-50 ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}

// ==================== MAIN PAGE ====================
export default function TimetableFullPage({ token }) {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('grid');

  const tabs = [
    { id: 'grid', label: language === 'hi' ? 'टाइमटेबल' : 'Timetable', icon: '📅' },
    { id: 'teachers', label: language === 'hi' ? 'शिक्षक शेड्यूल' : 'Teacher Schedule', icon: '👩‍🏫' },
    { id: 'setup', label: language === 'hi' ? 'पीरियड सेटअप' : 'Period Setup', icon: '⚙️' },
  ];

  return (
    <div className="space-y-4" data-testid="timetable-page">
      <div>
        <h1 className="text-xl font-bold text-gray-900">
          {language === 'hi' ? 'टाइमटेबल प्रबंधन' : 'Timetable Management'}
        </h1>
        <p className="text-sm text-gray-500">
          {language === 'hi' ? 'कक्षा-वार टाइमटेबल और शिक्षक शेड्यूल' : 'Class-wise timetable & teacher schedules'}
        </p>
      </div>

      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit" data-testid="timetable-tabs">
        {tabs.map(tab => (
          <button key={tab.id} data-testid={`tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${activeTab === tab.id ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}>
            <span className="mr-1.5">{tab.icon}</span>{tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'grid' && <TimetableGridTab token={token} language={language} />}
      {activeTab === 'teachers' && <TeacherScheduleTab token={token} language={language} />}
      {activeTab === 'setup' && <PeriodSetupTab token={token} language={language} />}
    </div>
  );
}
